import { useState } from 'react';
import type { AssetFinancialBulkResponse } from '@siliconx/shared';
import { BULK_ENTRY_MAX_COUNT } from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { Button } from '@siliconx/shared/ui/Button';

/**
 * SIL-305 (W4, financials-hifi-flow-spec.md slice 3) - the assets-registry
 * bulk "Enter manually" form. One form applies per-unit to every selected
 * asset via POST /api/client/asset-financial/bulk (SIL-298, admin-only).
 *
 * Two UX invariants called out on the ticket, both enforced here:
 *   - the form labels itself "applies per unit to N assets" so nobody reads
 *     a per-unit price as a lot total.
 *   - per-row skip results are surfaced after submit - a silent partial
 *     success would corrupt someone's books without telling them.
 *
 * The 500-item cap (BULK_ENTRY_MAX_COUNT) is enforced client-side with a
 * clear message rather than letting the server 400 - selection past the cap
 * disables Apply entirely.
 */

const FIELD =
  'w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
const LABEL = 'block text-xs font-medium text-zinc-500 mb-1';

export function BulkManualEntryModal({
  assetIds,
  onClose,
  onApplied,
}: {
  assetIds: string[];
  onClose: () => void;
  /** Called after a request completes (whether or not every row was
   * accepted) so the caller can refresh the table / clear selection. */
  onApplied: () => void;
}) {
  const [purchasePriceUsd, setPurchasePriceUsd] = useState('');
  const [purchasedAt, setPurchasedAt] = useState('');
  const [poRef, setPoRef] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AssetFinancialBulkResponse | null>(null);

  const overCap = assetIds.length > BULK_ENTRY_MAX_COUNT;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (overCap) {
      setError(
        `${assetIds.length} assets selected - bulk manual entry is capped at ${BULK_ENTRY_MAX_COUNT}. Narrow your selection and try again.`,
      );
      return;
    }
    const price = Number(purchasePriceUsd);
    if (!purchasePriceUsd || !Number.isFinite(price) || price < 0) {
      setError('Enter a non-negative purchase price per unit.');
      return;
    }
    if (!purchasedAt) {
      setError('Purchase date is required.');
      return;
    }

    setSubmitting(true);
    try {
      const httpRes = await api.api.client['asset-financial'].bulk.$post({
        json: {
          assetIds,
          purchasePriceUsd: price,
          purchasedAt,
          poRef: poRef.trim() || null,
          overwrite,
        },
      });
      await throwIfNotOk(httpRes);
      const res = (await httpRes.json()) as unknown as AssetFinancialBulkResponse;
      setResult(res);
      onApplied();
    } catch (ex: unknown) {
      // The bulk route rejects the WHOLE request with a single uniform 404
      // when any id fails to resolve (id-list resolution rule) - surface
      // that as one clear error, never per-id detail.
      setError((ex as Error).message ?? 'Bulk entry failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-base font-semibold text-zinc-900">Enter purchase data manually</h2>
        <p className="mb-4 text-sm text-zinc-500">
          Applies per unit to <span className="font-semibold text-zinc-700">{assetIds.length}</span>{' '}
          {assetIds.length === 1 ? 'asset' : 'assets'}.
        </p>

        {result ? (
          <BulkEntryResultSummary result={result} onClose={onClose} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={LABEL}>Purchase price per unit (USD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={purchasePriceUsd}
                  onChange={(e) => { setPurchasePriceUsd(e.target.value); }}
                  className={FIELD}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className={LABEL}>Purchase date</label>
                <input
                  type="date"
                  required
                  value={purchasedAt}
                  onChange={(e) => { setPurchasedAt(e.target.value); }}
                  className={FIELD}
                />
              </div>
            </div>
            <div>
              <label className={LABEL}>PO reference (optional)</label>
              <input
                value={poRef}
                onChange={(e) => { setPoRef(e.target.value); }}
                className={FIELD}
                placeholder="PO-12345"
              />
            </div>
            <label className="flex items-start gap-2 text-sm text-zinc-700">
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => { setOverwrite(e.target.checked); }}
                className="mt-0.5"
              />
              <span>
                Overwrite existing manual entries.
                <span className="block text-xs text-zinc-500">
                  Assets already carrying an ERP or CSV-imported entry are never touched from this form,
                  overwrite or not.
                </span>
              </span>
            </label>

            {overCap && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {assetIds.length} assets selected - bulk manual entry is capped at{' '}
                {BULK_ENTRY_MAX_COUNT}. Narrow your selection to continue.
              </p>
            )}
            {error && <p className="text-xs text-red-600">{error}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100"
              >
                Cancel
              </button>
              <Button className="disabled:cursor-not-allowed"
                type="submit"
                disabled={submitting || overCap}
              >
                {submitting ? 'Applying…' : `Apply to ${assetIds.length} assets`}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** Per-row skip results surfaced after submit - never a silent partial
 * success (spec: assets already carrying a manual row, or an ERP/CSV row
 * overwrite cannot touch, are reported here by row number). */
function BulkEntryResultSummary({
  result,
  onClose,
}: {
  result: AssetFinancialBulkResponse;
  onClose: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
        <span className="font-semibold text-emerald-700">{result.assetCountAccepted}</span> applied,{' '}
        <span className="font-semibold text-amber-700">{result.assetCountSkipped}</span> skipped out of{' '}
        {result.assetCountTotal} selected.
      </div>
      {result.rowErrors.length > 0 && (
        <div className="max-h-56 overflow-y-auto rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-800">
            Skipped rows
          </p>
          <ul className="space-y-1 text-xs text-amber-800">
            {result.rowErrors.map((r) => (
              <li key={r.rowNum}>
                Row {r.rowNum}: {r.warning ?? r.errors?.join('; ') ?? 'skipped'}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex justify-end">
        <Button
          onClick={onClose}
        >
          Done
        </Button>
      </div>
    </div>
  );
}
